import { useState, useEffect, useMemo, createContext, useContext, type ReactNode } from "react"
import { lumi } from "../lib/lumi"
import { fetchProfilesByUserId, createProfileIfMissing } from "../lib/userProfile"

// ==================== Auth Context (싱글톤) ====================
// useAuth가 여러 컴포넌트에서 중복 호출되어 userProfiles3.list가 6~7회씩
// 반복 조회되던 문제를 해결하기 위해, 인증/프로필 상태를 Context로 끌어올려
// 앱 전체에서 단 한 번만 조회/구독하도록 한다.
type AuthContextValue = ReturnType<typeof useAuthState>

const AuthContext = createContext<AuthContextValue | null>(null)

// 실제 인증 상태/로직을 담는 내부 훅 (Provider에서만 1회 호출)
function useAuthState() {
  const [user, setUser] = useState<any>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [profile, setProfile] = useState<any>(null)
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  // 프로필 조회 자체가 실패했는지 여부 (프로필 없음과 구분)
  const [profileLoadFailed, setProfileLoadFailed] = useState(false)
  // 같은 userId에 프로필이 2건 이상 존재하는지 여부
  const [hasDuplicateProfile, setHasDuplicateProfile] = useState(false)

  // 초기 인증 상태 확인 (페이지 로드 시)
  useEffect(() => {
    const initialize = async () => {
      if (lumi.auth.isAuthenticated && lumi.auth.user) {
        setUser(lumi.auth.user)
        setIsAuthenticated(true)
        
        // Admin은 프로필 체크 없이 바로 초기화 완료
        if (lumi.auth.user.userRole === "ADMIN") {
          setIsInitialized(true)
        } else {
          // 초기 로드 시 프로필 한 번만 조회
          await loadProfile(lumi.auth.user.userId)
        }
      } else {
        // 로그인되지 않은 상태는 바로 초기화 완료
        setIsInitialized(true)
      }
    }

    initialize()

    // 인증 상태 변경 리스너
    const unsubscribe = lumi.auth.onAuthChange(async ({ isAuthenticated, user }) => {
      console.log("🔔 onAuthChange 이벤트:", { isAuthenticated, userId: user?.userId })
      setUser(user)
      setIsAuthenticated(isAuthenticated)
      
      if (!isAuthenticated || !user) {
        // 로그아웃 시 모든 상태 초기화
        setProfile(null)
        setIsInitialized(true)
      } else if (user.userRole !== "ADMIN") {
        // 로그인 성공 시 프로필 로드 (Admin 제외)
        console.log("🔄 로그인 성공 - 프로필 로드 시작")
        await loadProfile(user.userId)
      } else {
        // Admin 로그인 시
        setIsInitialized(true)
      }
    })
    
    return unsubscribe
  }, [])

  // 프로필 로드 함수
  async function loadProfile(userId: string) {
    setIsLoadingProfile(true)
    setIsInitialized(false) // 로딩 시작 시 초기화 상태 해제
    
    try {
      console.log("🔍 프로필 조회 시작:", userId)
      // limit 1 대신 전체 조회 후 대표 프로필을 선택한다.
      // 중복 레코드가 있어도 항상 동일한(가장 유효한) 프로필이 잡히도록 보장.
      const lookup = await fetchProfilesByUserId(userId, {
        userId: 1,
        email: 1,
        userName: 1,
        teamId: 1,
        teamName: 1,
        hasSelectedTeam: 1,
        approvalStatus: 1,
        role: 1,
        enabled: 1,
        createdAt: 1
      })

      setHasDuplicateProfile(lookup.hasDuplicates)

      if (!lookup.ok) {
        // 조회 실패: 프로필 없음으로 오판하지 않도록 별도 플래그로 표시
        console.error("❌ 프로필 조회 실패 - 승인 상태를 판단할 수 없습니다.")
        setProfileLoadFailed(true)
        setProfile(null)
      } else if (lookup.primary) {
        console.log("✅ 프로필 로드 성공:", lookup.primary)
        setProfileLoadFailed(false)
        setProfile(lookup.primary)
      } else {
        console.log("⚠️ 프로필 없음 - 신규 사용자 또는 승인 대기")
        setProfileLoadFailed(false)
        setProfile(null)
      }
    } finally {
      setIsLoadingProfile(false)
      setIsInitialized(true) // 프로필 로딩 완료 후 초기화 완료
      console.log("✅ 프로필 로딩 완료 및 초기화 완료")
    }
  }

  // 프로필 새로고침 (팀 선택 완료 후 호출)
  const refreshProfile = async () => {
    if (user?.userId) {
      await loadProfile(user.userId)
    }
  }

  // 로그인 함수
  const signIn = async () => {
    try {
      const result = await lumi.auth.signIn()
      
      if (!result || !result.user) {
        throw new Error("로그인에 실패했습니다.")
      }
      
      // Admin은 프로필 체크 없이 바로 통과
      if (result.user.userRole === "ADMIN") {
        console.log("👑 Admin 로그인 - 프로필 체크 생략")
        return result
      }
      
      // 일반 사용자: 프로필 확인 (rejected 체크 및 중복 생성 방지)
      const lookup = await fetchProfilesByUserId(result.user.userId)

      if (!lookup.ok) {
        // 조회 실패 시에는 신규 생성도, rejected 차단도 하지 않는다.
        // (일시적 오류로 중복 계정이 생기거나 정상 사용자가 차단되는 것을 방지)
        console.warn("⚠️ 로그인 중 프로필 조회 실패 - 프로필 생성/차단 판정을 건너뜁니다.")
        return result
      }

      if (lookup.primary) {
        console.log("📋 기존 프로필 발견:", lookup.primary.approvalStatus)

        if (lookup.hasDuplicates) {
          console.warn(
            `⚠️ 중복 프로필 ${lookup.list.length}건 감지 - 대표 프로필로 진행합니다.`
          )
        }

        // rejected는 로그인 차단.
        // 단, 중복 레코드가 있을 때 한 건만 rejected인 경우로 정상 사용자가 차단되지 않도록,
        // "모든" 프로필이 rejected일 때만 차단한다.
        const allRejected = lookup.list.every((p) => p?.approvalStatus === "rejected")
        if (allRejected) {
          alert("❌ 로그인이 거부되었습니다.\n\n관리자에 의해 승인이 거부되었습니다.")
          await lumi.auth.signOut()
          throw new Error("Account rejected")
        }

        // onAuthChange가 프로필을 로드할 것이므로 여기서는 setProfile 호출하지 않음
      } else {
        // 신규 사용자 프로필 생성 (내부에서 재조회해 중복을 한 번 더 차단)
        console.log("🆕 신규 사용자 - 프로필 생성 시도")
        const outcome = await createProfileIfMissing({
          userId: result.user.userId,
          email: result.user.email
        })

        if (!outcome.created) {
          console.log(`ℹ️ 프로필 신규 생성 생략 (사유: ${outcome.blocked})`)
        }
        // onAuthChange가 프로필을 로드할 것이므로 여기서는 setProfile 호출하지 않음
      }
      
      return result
    } catch (error: any) {
      console.error("로그인 오류:", error)
      if (error.message === "Account rejected") {
        throw error
      }
      alert("로그인 중 오류가 발생했습니다.\n다시 시도해주세요.")
      throw error
    }
  }

  // 로그아웃 함수
  const signOut = async () => {
    try {
      await lumi.auth.signOut()
      setUser(null)
      setIsAuthenticated(false)
      setProfile(null)
      setProfileLoadFailed(false)
      setHasDuplicateProfile(false)
    } catch (error) {
      console.error("로그아웃 실패:", error)
    }
  }

  // 계산된 값들 (4단계 최적화: 파생값을 useMemo로 메모이즈하여
  // consumer 컴포넌트의 불필요한 재계산/리렌더를 줄인다)
  return useMemo(() => {
    const isAdmin = user?.userRole === "ADMIN"
    const approvalStatus = profile?.approvalStatus || "pending"
    const userRole = profile?.role || "USER"
    const teamId = profile?.teamId || ""
    const teamName = profile?.teamName || ""
    // userName: profile의 userName이 비어있으면 user의 userName 또는 email 사용
    const userName = profile?.userName || user?.userName || user?.email?.split("@")[0] || ""
    const hasSelectedTeam = profile?.hasSelectedTeam || false

    return {
      user,
      isAuthenticated,
      profile,
      isLoadingProfile,
      isInitialized,
      // 프로필 조회 실패 여부 (true면 승인상태를 신뢰할 수 없음)
      profileLoadFailed,
      // 중복 프로필 감지 여부
      hasDuplicateProfile,

      // 승인 및 역할 상태
      approvalStatus,
      isAdmin,
      userRole,
      isCLevel: userRole === "C_LEVEL",
      isManager: userRole === "MANAGER",
      isUser: userRole === "USER",

      // 팀 정보
      teamId,
      teamName,
      userName,
      hasSelectedTeam,

      // 함수
      signIn,
      signOut,
      refreshProfile,
      // 하위 호환 별칭: 기존 컴포넌트들이 refreshUserProfile를 사용하므로 유지
      refreshUserProfile: refreshProfile,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAuthenticated, profile, isLoadingProfile, isInitialized, profileLoadFailed, hasDuplicateProfile])
}

// ==================== Provider & Consumer Hook ====================
// 앱 루트에서 AuthProvider로 감싸면, 내부의 모든 useAuth() 호출은
// 동일한 단일 인증/프로필 상태를 공유한다 (중복 API 호출 제거).
export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useAuthState()
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth는 AuthProvider 내부에서만 사용할 수 있습니다.")
  }
  return ctx
}

/* ==================== 기존 코드 (백업용) ====================
import { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"

export function useAuth() {
  const [user, setUser] = useState<any>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [teamId, setTeamId] = useState<string>("")
  const [teamName, setTeamName] = useState<string>("")
  const [userName, setUserName] = useState<string>("")
  const [userRole, setUserRole] = useState<string>("")

  useEffect(() => {
    // 초기 인증 상태 확인 및 팀 정보 로드
    if (lumi.auth.isAuthenticated && lumi.auth.user) {
      setUser(lumi.auth.user)
      setIsAuthenticated(true)
      // 로그인되어 있으면 팀 정보 자동 로드
      loadUserTeam(lumi.auth.user.userId)
    }

    // 인증 상태 변경 리스너 등록
    const unsubscribe = lumi.auth.onAuthChange(({ isAuthenticated, user }) => {
      setUser(user)
      setIsAuthenticated(isAuthenticated)
      if (isAuthenticated && user) {
        // 로그인 시 팀 정보 로드
        loadUserTeam(user.userId)
      } else {
        // 로그아웃 시 팀 정보 초기화
        setTeamId("")
        setTeamName("")
        setUserName("")
        setUserRole("")
      }
    })
    
    return unsubscribe
  }, [])

  async function loadUserTeam(userId: string) {
    try {
      const result = await lumi.entities.userProfiles3.list({
        filter: { userId },
        limit: 1
      })
      if (result.list && result.list.length > 0) {
        setTeamId(result.list[0].teamId || "")
        setTeamName(result.list[0].teamName || "")
        setUserName(result.list[0].userName || "")
        setUserRole(result.list[0].role || "USER")
      }
    } catch (error) {
      console.error("팀 정보 로드 실패:", error)
    }
  }

  const refreshUserProfile = async () => {
    if (user?.userId) {
      await loadUserTeam(user.userId)
    }
  }

  const signIn = async () => {
    try {
      const result = await lumi.auth.signIn()
      
      // 인증 실패
      if (!result || !result.user) {
        throw new Error("로그인에 실패했습니다.")
      }
      
      // 관리자는 승인 체크 없이 바로 로그인
      if (result.user.userRole === "ADMIN") {
        return result
      }
      
      // 일반 사용자: userProfiles3 생성 또는 승인 상태 확인
      if (result.user.userId) {
        try {
          const profileResult = await lumi.entities.userProfiles3.list({
            filter: { userId: result.user.userId },
            limit: 1
          })
          
          if (profileResult.list && profileResult.list.length > 0) {
            // 기존 프로필이 있는 경우: 승인 상태 확인
            const profile = profileResult.list[0]
            
            if (profile.approvalStatus === "rejected") {
              alert("❌ 로그인이 거부되었습니다.\n\n관리자에 의해 승인이 거부되었습니다.")
              await lumi.auth.signOut()
              throw new Error("Account rejected")
            }
            
            // pending이거나 approved든 일단 로그인 진행 (App.tsx에서 처리)
            return result
          } else {
            // 신규 사용자: userProfiles3 자동 생성 (모든 사용자 pending 상태)
            console.log("🆕 신규 사용자 프로필 생성 중...", result.user.userId)
            const newProfile = await lumi.entities.userProfiles3.create({
              userId: result.user.userId,
              email: result.user.email,
              userName: "",
              teamId: "",
              teamName: "",
              hasSelectedTeam: false,
              approvalStatus: "pending",
              enabled: true,
              role: "USER",
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            })
            
            console.log("✅ 신규 사용자 프로필 생성 완료:", {
              userId: newProfile.userId,
              approvalStatus: newProfile.approvalStatus,
              _id: newProfile._id
            })
            
            // 프로필 생성 후 약간의 대기 시간 (DB 동기화)
            await new Promise(resolve => setTimeout(resolve, 500))
            
            // 승인 대기 상태로 계속 진행
            return result
          }
        } catch (profileError) {
          console.error("프로필 생성/확인 실패:", profileError)
          // 프로필 생성 실패해도 인증은 성공했으므로 계속 진행
          // App.tsx에서 다시 프로필 체크 시도
          return result
        }
      }
      
      return result
    } catch (error: any) {
      console.error("로그인 오류:", error)
      // 사용자가 인증 창을 닫은 경우나 도메인 오류는 throw
      if (error.message === "Auth window closed" || 
          error.message === "Invalid email domain" ||
          error.message === "Account rejected") {
        throw error
      }
      // 기타 오류는 사용자에게 알림
      alert("로그인 중 오류가 발생했습니다.\n다시 시도해주세요.")
      throw error
    }
  }

  const signOut = async () => {
    try {
      await lumi.auth.signOut()
      setUser(null)
      setIsAuthenticated(false)
      setTeamId("")
      setTeamName("")
      setUserName("")
      setUserRole("")
    } catch (error) {
      console.error("로그아웃 실패:", error)
    }
  }

  return {
    user,
    isAuthenticated,
    isAdmin: user?.userRole === "ADMIN",
    isUser: user?.userRole === "USER",
    teamId,
    teamName,
    userName,
    userRole,
    isCLevel: userRole === "C_LEVEL",
    isManager: userRole === "MANAGER",
    signIn,
    signOut,
    refreshUserProfile,
    // App.tsx에서 직접 팀 정보를 설정할 수 있도록 setter 함수들 export
    setTeamId,
    setTeamName,
    setUserName,
    setUserRole,
  }
}
*/
